/**
 * Audio Analysis Utilities
 * Pure TypeScript DSP functions for BPM detection, key estimation, and energy segmentation.
 * Uses Web Audio API AudioBuffer as input.
 */

import { BpmEstimate, KeyEstimate, EnergySegment } from '@/types/referenceTrack.types';

// ============================================
// Constants
// ============================================

const NOTE_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

// Krumhansl-Schmuckler key profiles
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

// BPM detection range
const MIN_BPM = 60;
const MAX_BPM = 200;

// Energy segmentation
const ENERGY_SEGMENTS = 20; // Number of segments to divide the track into

// ============================================
// Mono Mixdown
// ============================================

/**
 * Mix all channels down to a single mono Float32Array
 */
function getMono(buffer: AudioBuffer): Float32Array {
  const length = buffer.length;
  const mono = new Float32Array(length);

  if (buffer.numberOfChannels === 1) {
    buffer.copyFromChannel(mono, 0);
    return mono;
  }

  // Average all channels
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      mono[i] += channelData[i];
    }
  }

  const scale = 1 / buffer.numberOfChannels;
  for (let i = 0; i < length; i++) {
    mono[i] *= scale;
  }

  return mono;
}

// ============================================
// BPM Detection (Autocorrelation)
// ============================================

/**
 * Compute onset strength envelope from audio samples.
 * Uses spectral flux approximation via windowed energy differences.
 */
function computeOnsetEnvelope(samples: Float32Array, sampleRate: number): { envelope: Float32Array; hopRate: number } {
  const hopSize = Math.round(sampleRate * 0.01); // 10ms hops
  const windowSize = hopSize * 2;
  const numFrames = Math.floor((samples.length - windowSize) / hopSize);
  const envelope = new Float32Array(numFrames);
  const hopRate = sampleRate / hopSize;

  let prevEnergy = 0;
  for (let i = 0; i < numFrames; i++) {
    const start = i * hopSize;
    let energy = 0;
    for (let j = start; j < start + windowSize && j < samples.length; j++) {
      energy += samples[j] * samples[j];
    }
    energy /= windowSize;

    // Half-wave rectified spectral flux approximation
    const flux = Math.max(0, energy - prevEnergy);
    envelope[i] = flux;
    prevEnergy = energy;
  }

  return { envelope, hopRate };
}

/**
 * Autocorrelation-based BPM estimation.
 * Returns the most likely BPM with confidence and alternates.
 */
export function estimateBpm(buffer: AudioBuffer): BpmEstimate {
  const mono = getMono(buffer);
  const { envelope, hopRate } = computeOnsetEnvelope(mono, buffer.sampleRate);

  // Normalize envelope
  let maxVal = 0;
  for (let i = 0; i < envelope.length; i++) {
    if (envelope[i] > maxVal) maxVal = envelope[i];
  }
  if (maxVal > 0) {
    for (let i = 0; i < envelope.length; i++) {
      envelope[i] /= maxVal;
    }
  }

  // Compute autocorrelation for lag range corresponding to BPM range
  const minLag = Math.floor((60 / MAX_BPM) * hopRate);
  const maxLag = Math.ceil((60 / MIN_BPM) * hopRate);
  const correlations: { lag: number; value: number }[] = [];

  for (let lag = minLag; lag <= maxLag && lag < envelope.length; lag++) {
    let sum = 0;
    let count = 0;
    for (let i = 0; i < envelope.length - lag; i++) {
      sum += envelope[i] * envelope[i + lag];
      count++;
    }
    if (count > 0) {
      correlations.push({ lag, value: sum / count });
    }
  }

  if (correlations.length === 0) {
    return { bpm: 120, confidence: 0, alternates: [] };
  }

  // Sort by correlation value (descending)
  correlations.sort((a, b) => b.value - a.value);

  // Find the top peaks (avoiding harmonics)
  const peaks: { bpm: number; confidence: number }[] = [];
  const maxCorr = correlations[0].value;

  for (const c of correlations) {
    const bpm = Math.round((60 * hopRate) / c.lag);
    if (bpm < MIN_BPM || bpm > MAX_BPM) continue;

    const confidence = maxCorr > 0 ? c.value / maxCorr : 0;

    // Check if this BPM is too close to an existing peak
    const tooClose = peaks.some(p => Math.abs(p.bpm - bpm) < 5);
    if (!tooClose) {
      peaks.push({ bpm, confidence });
      if (peaks.length >= 4) break;
    }
  }

  if (peaks.length === 0) {
    return { bpm: 120, confidence: 0, alternates: [] };
  }

  return {
    bpm: peaks[0].bpm,
    confidence: Math.min(1, peaks[0].confidence),
    alternates: peaks.slice(1)
  };
}

// ============================================
// Key Estimation (Krumhansl-Schmuckler)
// ============================================

/**
 * Compute chroma vector from audio using simple DFT at note frequencies.
 * Analyzes the full audio to build a 12-bin pitch class distribution.
 */
function computeChroma(samples: Float32Array, sampleRate: number): Float32Array {
  const chroma = new Float32Array(12);

  // Use windowed segments for better accuracy
  const segmentSize = Math.min(8192, samples.length);
  const numSegments = Math.floor(samples.length / segmentSize);

  for (let seg = 0; seg < numSegments; seg++) {
    const offset = seg * segmentSize;

    for (let note = 0; note < 12; note++) {
      // Analyze multiple octaves (octaves 2-6, ~65Hz to ~2093Hz)
      for (let octave = 2; octave <= 6; octave++) {
        const freq = 440 * Math.pow(2, (note - 9 + (octave - 4) * 12) / 12);
        if (freq > sampleRate / 2) continue;

        // Goertzel-like single-frequency DFT
        const period = sampleRate / freq;
        const windowLen = Math.min(Math.round(period * 4), segmentSize);

        let sumReal = 0;
        let sumImag = 0;
        const omega = (2 * Math.PI * freq) / sampleRate;

        for (let i = 0; i < windowLen; i++) {
          const idx = offset + i;
          if (idx >= samples.length) break;
          // Apply Hanning window
          const window = 0.5 * (1 - Math.cos((2 * Math.PI * i) / windowLen));
          const sample = samples[idx] * window;
          sumReal += sample * Math.cos(omega * i);
          sumImag += sample * Math.sin(omega * i);
        }

        const magnitude = Math.sqrt(sumReal * sumReal + sumImag * sumImag) / windowLen;
        chroma[note] += magnitude;
      }
    }
  }

  // Normalize
  let maxChroma = 0;
  for (let i = 0; i < 12; i++) {
    if (chroma[i] > maxChroma) maxChroma = chroma[i];
  }
  if (maxChroma > 0) {
    for (let i = 0; i < 12; i++) {
      chroma[i] /= maxChroma;
    }
  }

  return chroma;
}

/**
 * Pearson correlation between two arrays
 */
function pearsonCorrelation(a: Float32Array | number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  let sumA = 0, sumB = 0, sumAB = 0, sumA2 = 0, sumB2 = 0;

  for (let i = 0; i < n; i++) {
    sumA += a[i];
    sumB += b[i];
    sumAB += a[i] * b[i];
    sumA2 += a[i] * a[i];
    sumB2 += b[i] * b[i];
  }

  const numerator = n * sumAB - sumA * sumB;
  const denominator = Math.sqrt((n * sumA2 - sumA * sumA) * (n * sumB2 - sumB * sumB));

  return denominator === 0 ? 0 : numerator / denominator;
}

/**
 * Rotate an array by shift positions
 */
function rotateArray(arr: number[], shift: number): number[] {
  const n = arr.length;
  const result = new Array(n);
  for (let i = 0; i < n; i++) {
    result[i] = arr[(i + shift) % n];
  }
  return result;
}

/**
 * Estimate musical key using Krumhansl-Schmuckler algorithm.
 * Computes chroma vector and correlates against major/minor key profiles.
 */
export function estimateKey(buffer: AudioBuffer): KeyEstimate {
  const mono = getMono(buffer);
  const chroma = computeChroma(mono, buffer.sampleRate);

  const results: { root: string; mode: 'major' | 'minor'; correlation: number }[] = [];

  for (let shift = 0; shift < 12; shift++) {
    const majorCorr = pearsonCorrelation(chroma, rotateArray(MAJOR_PROFILE, shift));
    const minorCorr = pearsonCorrelation(chroma, rotateArray(MINOR_PROFILE, shift));

    results.push({
      root: NOTE_NAMES[shift],
      mode: 'major',
      correlation: majorCorr
    });
    results.push({
      root: NOTE_NAMES[shift],
      mode: 'minor',
      correlation: minorCorr
    });
  }

  // Sort by correlation (descending)
  results.sort((a, b) => b.correlation - a.correlation);

  // Normalize confidence: map top correlation to 0-1 range
  const maxCorr = results[0].correlation;
  const minCorr = results[results.length - 1].correlation;
  const range = maxCorr - minCorr;

  const toConfidence = (corr: number) => range > 0 ? Math.max(0, Math.min(1, (corr - minCorr) / range)) : 0.5;

  const top = results[0];
  const alternates = results.slice(1, 4).map(r => ({
    root: r.root,
    mode: r.mode,
    confidence: toConfidence(r.correlation)
  }));

  return {
    root: top.root,
    mode: top.mode,
    confidence: toConfidence(top.correlation),
    displayName: `${top.root} ${top.mode === 'major' ? 'Major' : 'Minor'}`,
    alternates
  };
}

// ============================================
// Energy Segmentation
// ============================================

/**
 * Compute RMS energy for each segment of the audio.
 * Returns normalized energy values mapped to 0-100 scale.
 */
export function computeEnergySegments(buffer: AudioBuffer, numSegments: number = ENERGY_SEGMENTS): EnergySegment[] {
  const mono = getMono(buffer);
  const samplesPerSegment = Math.floor(mono.length / numSegments);
  const segments: EnergySegment[] = [];

  let maxRms = 0;
  const rmsValues: number[] = [];

  // Compute RMS for each segment
  for (let i = 0; i < numSegments; i++) {
    const start = i * samplesPerSegment;
    const end = Math.min(start + samplesPerSegment, mono.length);
    let sumSquares = 0;
    let count = 0;

    for (let j = start; j < end; j++) {
      sumSquares += mono[j] * mono[j];
      count++;
    }

    const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
    rmsValues.push(rms);
    if (rms > maxRms) maxRms = rms;
  }

  // Normalize to 0-100 and build segments
  for (let i = 0; i < numSegments; i++) {
    const normalizedEnergy = maxRms > 0 ? Math.round((rmsValues[i] / maxRms) * 100) : 0;
    const timeSeconds = (i * samplesPerSegment) / buffer.sampleRate;
    const position = Math.round((i / (numSegments - 1)) * 100);

    segments.push({
      position: Math.min(100, position),
      energy: normalizedEnergy,
      timeSeconds: Math.round(timeSeconds * 10) / 10
    });
  }

  return segments;
}

// ============================================
// Full Analysis Pipeline
// ============================================

/**
 * Run complete audio analysis on an AudioBuffer.
 * Returns BPM, key, and energy segmentation results.
 */
export function analyzeAudioBuffer(buffer: AudioBuffer, filename: string) {
  const bpm = estimateBpm(buffer);
  const key = estimateKey(buffer);
  const energy = computeEnergySegments(buffer);

  return {
    filename,
    duration: buffer.duration,
    sampleRate: buffer.sampleRate,
    bpm,
    key,
    energy,
    analyzedAt: new Date().toISOString()
  };
}
